import React, { useMemo, useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { AlertTriangle, MapPin, Radio, X } from 'lucide-react';

// Public CDN topojson — small, cached forever
const WORLD_TOPO = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

const SEVERITY_TONE = {
  critical: { fill: '#f43f5e', ring: 'rgba(244, 63, 94, 0.35)' }, // rose
  suspicious: { fill: '#f59e0b', ring: 'rgba(245, 158, 11, 0.35)' }, // amber
  blocked: { fill: '#ef4444', ring: 'rgba(239, 68, 68, 0.45)' }, // red
  normal: { fill: '#22d3ee', ring: 'rgba(34, 211, 238, 0.28)' }, // cyan
};

/**
 * ThreatMap — pins every recently active IP on a world map using the geo data
 * captured by the audit middleware (lat/lon from ip-api). Hovering shows
 * details, and hostile IPs pulse.
 */
const ThreatMap = ({ ips = [] }) => {
  const [hoverIp, setHoverIp] = useState(null);

  const pinned = useMemo(() => (
    (ips || [])
      .filter(row => row.geo && typeof row.geo.lat === 'number' && typeof row.geo.lon === 'number')
      .map(row => {
        let tone = 'normal';
        if (row.is_blocked) tone = 'blocked';
        else if (row.suspicious) tone = 'suspicious';
        else if (row.failed_events && row.failed_events >= 3) tone = 'critical';
        return { ...row, tone };
      })
  ), [ips]);

  const stats = useMemo(() => ({
    total: pinned.length,
    hostile: pinned.filter(p => p.tone !== 'normal').length,
    countries: new Set(pinned.map(p => p.geo?.country_code).filter(Boolean)).size,
  }), [pinned]);

  return (
    <div className="rounded-2xl border border-cyan-500/25 bg-gradient-to-br from-[#050510] via-[#0b0a1a] to-[#050510] overflow-hidden" data-testid="threat-map">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b border-white/[0.05]">
        <div className="flex items-center gap-3">
          <div className="relative w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
            <Radio className="w-5 h-5 text-cyan-300" />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.7)] animate-pulse" />
          </div>
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-cyan-300/70">Live World View</p>
            <h3 className="text-white font-semibold text-base">Threat Map</h3>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-lg bg-cyan-500/10 border border-cyan-500/25 text-cyan-200"><MapPin className="w-3 h-3 inline mr-1" />{stats.total} pins</span>
          <span className="px-2.5 py-1 rounded-lg bg-rose-500/10 border border-rose-500/25 text-rose-200"><AlertTriangle className="w-3 h-3 inline mr-1" />{stats.hostile} hostile</span>
          <span className="px-2.5 py-1 rounded-lg bg-white/[0.03] border border-white/10 text-slate-300">{stats.countries} countries</span>
        </div>
      </div>

      {/* Map */}
      <div className="relative h-[420px] bg-[#04040c] overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: 'radial-gradient(rgba(34,211,238,0.6) 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }} />
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{ scale: 130, center: [0, 30] }}
          style={{ width: '100%', height: '100%' }}
        >
          <ZoomableGroup maxZoom={6} minZoom={1}>
            <Geographies geography={WORLD_TOPO}>
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill="#0e1024"
                    stroke="#1e2340"
                    strokeWidth={0.35}
                    style={{
                      default: { outline: 'none' },
                      hover:   { fill: '#161a34', outline: 'none' },
                      pressed: { fill: '#0e1024', outline: 'none' },
                    }}
                  />
                ))
              }
            </Geographies>

            {pinned.map((row, i) => {
              const tone = SEVERITY_TONE[row.tone];
              const isHostile = row.tone !== 'normal';
              const size = row.count > 20 ? 6 : row.count > 5 ? 4.5 : 3.5;
              return (
                <Marker key={i} coordinates={[row.geo.lon, row.geo.lat]}
                  onMouseEnter={() => setHoverIp(row)}
                  onMouseLeave={() => setHoverIp(null)}>
                  {/* Outer ring — pulses on hostile */}
                  <circle
                    r={size + 3}
                    fill={tone.ring}
                    style={isHostile ? { animation: 'pulse 1.8s ease-in-out infinite' } : undefined}
                  />
                  {/* Core dot */}
                  <circle
                    r={size}
                    fill={tone.fill}
                    stroke="#0a0a14"
                    strokeWidth={0.6}
                    style={{ cursor: 'pointer', filter: `drop-shadow(0 0 6px ${tone.fill})` }}
                    data-testid={`threat-pin-${i}`}
                  />
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>

        {/* Hover tooltip */}
        {hoverIp && (
          <div className="absolute top-3 left-3 max-w-[300px] rounded-xl border border-white/10 bg-[#0a0a14]/95 backdrop-blur-md p-3 shadow-xl pointer-events-none">
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`w-2 h-2 rounded-full ${hoverIp.tone === 'normal' ? 'bg-cyan-400' : hoverIp.tone === 'suspicious' ? 'bg-amber-400' : 'bg-rose-400'}`} />
              <span className="font-mono text-sm text-white">{hoverIp.ip}</span>
            </div>
            <p className="text-xs text-slate-300">
              {hoverIp.geo?.city ? `${hoverIp.geo.city}, ` : ''}{hoverIp.geo?.country}
              {hoverIp.geo?.country_code && ` (${hoverIp.geo.country_code})`}
            </p>
            {hoverIp.geo?.isp && <p className="text-[11px] text-slate-500 mt-0.5 truncate">{hoverIp.geo.isp}</p>}
            <div className="flex flex-wrap gap-1 mt-2">
              <span className="px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-300 text-[10px]">{hoverIp.count} events</span>
              {hoverIp.failed_events > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 text-[10px]">{hoverIp.failed_events} suspicious</span>
              )}
              {hoverIp.geo?.is_hosting && <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-300 text-[10px]">Hosting</span>}
              {hoverIp.geo?.is_proxy && <span className="px-1.5 py-0.5 rounded bg-rose-500/10 text-rose-300 text-[10px]">Proxy</span>}
              {hoverIp.is_blocked && <span className="px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-200 text-[10px]">Blocked</span>}
            </div>
            {hoverIp.unique_users?.length > 0 && (
              <p className="text-[10px] text-slate-500 mt-2 truncate">
                <span className="text-cyan-300">Sign-ins:</span> {hoverIp.unique_users.slice(0, 4).join(', ')}
              </p>
            )}
          </div>
        )}

        {/* Legend */}
        <div className="absolute bottom-3 right-3 flex flex-col gap-1 text-[11px] px-3 py-2 rounded-lg bg-[#0a0a14]/90 backdrop-blur-md border border-white/[0.06]">
          <p className="text-[9px] tracking-[0.2em] uppercase text-slate-500 mb-0.5">Threat Level</p>
          <div className="flex items-center gap-1.5 text-cyan-300"><span className="w-2 h-2 rounded-full bg-cyan-400" /> Normal</div>
          <div className="flex items-center gap-1.5 text-amber-300"><span className="w-2 h-2 rounded-full bg-amber-400" /> Suspicious</div>
          <div className="flex items-center gap-1.5 text-rose-300"><span className="w-2 h-2 rounded-full bg-rose-500" /> Blocked</div>
        </div>

        {pinned.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-slate-500 text-sm">Waiting for geolocated traffic…</p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%   { transform: scale(1);   opacity: 0.7; }
          50%  { transform: scale(1.6); opacity: 0.2; }
          100% { transform: scale(1);   opacity: 0.7; }
        }
      `}</style>
    </div>
  );
};

export default ThreatMap;
